import moment from "moment";
import { useState } from "react";
import { useDate, useI18N } from "../common/common";
import { dateToUTCTimezone, getYearMonthDayFormat, weekdaysMap } from "../common/date";
import { useBookingPeriod } from "./useBookingPeriod";

export const useBookingDisabledCalendarDates = (resortDetails, selectedDays) => {
    const {bookingPeriodEndDate} = useBookingPeriod();
    const {i18n} = useI18N();
    const {formatDate} = useDate();

    const [disabledCalendarDays, setDisabledCalendarDays] = useState(undefined);

    const getBookingPeriodEndLabel = () => {
        return formatDate(bookingPeriodEndDate, {month: "long"}) + " " + i18n("dialog.search_bar.search_resorts.date.snackbar");
    };

    const calculateDisabledCalendarDays = () => {
        const disabledDatesMap = new Map();

        const now = moment();

        const endOfBookingPeriod = moment(dateToUTCTimezone(bookingPeriodEndDate));

        // If date from is in the last month prior to the end of the booking period, then extend the booking period by 10 days
        if (!selectedDays?.date_to && moment(selectedDays.date_from)?.isSame(endOfBookingPeriod, "month")) {
            endOfBookingPeriod.add(10, "d");
        }

        const firstCalendarDay = moment().startOf("month");
        const lastCalendarDay = endOfBookingPeriod.clone().startOf("month")
            .add(3, "months")
            .endOf("month");

        // Disable dates before now
        for (const i = firstCalendarDay; i.isBefore(now.clone().subtract(1, "days")); i.add(1, "days")) {
            disabledDatesMap.set(getYearMonthDayFormat(i), {info: ""});
        }

        // Disable dates after end of booking period
        for (const i = lastCalendarDay; i.isAfter(endOfBookingPeriod.clone().add(1, "days")); i.subtract(1, "days")) {
            disabledDatesMap.set(getYearMonthDayFormat(i), {info: getBookingPeriodEndLabel()});
        }

        // Disable closing times dates
        resortDetails?.closingTimesCollection?.items.forEach((times) => {
            const from = moment(dateToUTCTimezone(times?.from?.split("T")[0]));
            const fromPlusOneDay = from.add(1, "days");
            const to = moment(dateToUTCTimezone(times?.to?.split("T")[0]));
            const toPlusOneDay = to.add(1, "days");
            for (let i = fromPlusOneDay; i.isBefore(toPlusOneDay); i.add(1, "days")) {
                disabledDatesMap.set(getYearMonthDayFormat(i), {info: times?.information});
            }
        });

        // Disable weekdays with arrival restrictions
        if (resortDetails?.arrivalRestrictions?.weekdaysWithArrivalRestrictions?.length > 0) {
            const daysArray = resortDetails?.arrivalRestrictions?.weekdaysWithArrivalRestrictions?.map((theDay) => {
                return weekdaysMap[theDay];
            });

            for (let i = firstCalendarDay; i.isBefore(lastCalendarDay); i.add(1, "days")) {
                if (daysArray.includes(i.day())) {
                    disabledDatesMap.set(getYearMonthDayFormat(i), {info: ""});
                }
            }
        }

        // Check min stay restrictions
        if (selectedDays?.date_from && resortDetails?.resortStayRestrictions?.length) {
            const d = moment(selectedDays.date_from);

            let stayRestriction = undefined;
            for (const restriction of resortDetails.resortStayRestrictions) {
                const restrictionStartDate = moment(restriction.dateFrom);
                const restrictionEndDate = moment(restriction.dateTo);

                if (d.isBetween(restrictionStartDate, restrictionEndDate, undefined, "[]")) {
                    stayRestriction = restriction;
                    break;
                }
            }

            if (stayRestriction?.minNights) {
                for (let i = 1; i < stayRestriction.minNights; i ++) {
                    const nextDay = moment(d.toDate());
                    nextDay.add(i, "d");
                    disabledDatesMap.set(disabledDatesMap.set(getYearMonthDayFormat(nextDay), {info: ""}));
                }
            }
        }

        setDisabledCalendarDays(disabledDatesMap);
    };

    const isSelectedBookingDaysUnavailable = () => {
        return disabledCalendarDays.has(selectedDays.dateFrom) || disabledCalendarDays.has(selectedDays.dateTo);
    };

    return {
        disabledCalendarDays,
        getBookingPeriodEndLabel,
        calculateDisabledCalendarDays,
        isSelectedBookingDaysUnavailable
    };
};
